import { BookingFormField } from '@domain/booking-form-field';
import { Price } from '@domain/price';
import { TicketHolderFormField } from '@domain/ticket-holder-form-field';

import { AdmissionType, Settings } from './admission';

export interface Channel {
  id: string;
  maxQuantity: string | number | null;
}

export interface Translations {
  [key: string]: string | null;
  ca: string | null;
  de: string | null;
  en: string | null;
  es: string | null;
  fr: string | null;
  it: string | null;
  pt: string | null;
}

interface ItemsPerTransaction {
  min: number;
  max: number;
}

interface Images {
  id: string;
  url: string;
  orderPosition: number;
}

export interface BasePreset {
  id: string;
  companyClubId: string;
  color: string | null;
  price: Price;
  quantity: number | null;
  name: Translations;
  description: Translations;
  itemsPerTransaction: ItemsPerTransaction;
  accessCode: string | null;
  bookingFormFields: BookingFormField[];
  images: Images[];
  settings: Settings;
  italyRegulation?: { dirittoPrevendita: number | null };
  ticketHolderFormFields: TicketHolderFormField[];
  updatedAt: number;
  drinksIncluded?: number;
  onlinePrice?: string;
  offlinePrice?: string;
  channels?: Channel[];
  admissionType: AdmissionType;
}

export interface InvitationPreset {
  id: string;
  companyClubId: string;
  name: Translations;
  description: Translations;
  bookingFormFields: BookingFormField[];
  settings: Settings;
  channels: Channel[];
}

export interface BottleServicePreset extends BasePreset {
  onlinePaymentPercentage: number;
  guestCapacity: number;
}

export interface TicketPreset extends BasePreset {
  onlinePaymentPercentage: number;
}

export type Preset = BasePreset | BottleServicePreset | TicketPreset;

export const PRESET_TYPE_BOTTLE_SERVICE = 'bottleService';
export const PRESET_TYPE_GUEST_LIST = 'guestList';
export const PRESET_TYPE_TICKET = 'ticket';

export type PresetType = typeof PRESET_TYPE_BOTTLE_SERVICE | typeof PRESET_TYPE_GUEST_LIST | typeof PRESET_TYPE_TICKET;

export interface PresetsRequest {
  bottleService: BottleServicePreset[];
  ticket: TicketPreset[];
  guestList: BasePreset[];
}

export type GetAdmissionsPresetByTypeResponse = BasePreset[] | BottleServicePreset[] | TicketPreset[];

export interface GetAdmissionsPresetResponse {
  [PRESET_TYPE_BOTTLE_SERVICE]: BottleServicePreset[];
  [PRESET_TYPE_GUEST_LIST]: BasePreset[];
  [PRESET_TYPE_TICKET]: TicketPreset[];
}

export interface CreateAdmissionPresetRequest {
  companyClubId?: string;
  price?: {
    amount?: number;
    currency: string;
    onlinePrice?: number;
    offlinePrice?: number;
  };
  quantity?: number | null;
  name?: object;
  description?: object;
  itemsPerTransaction?: {
    min?: number;
    max?: number;
  };
  onlinePaymentPercentage?: number;
  images?: string[];
  guestCapacity?: number;
  settings?: {
    channelDefaultMaxQuantity?: number | null;
    isForAllChannels?: boolean;
    isPrivate?: boolean;
    isNominative?: boolean;
  };
  italyRegulation?: {
    dirittoPrevendita: number | null;
  };
  bookingFormFields?: {
    name: string;
    dataType: string;
    isRequired: boolean;
  }[];
  ticketHolderFormFields?: {
    name: string;
    dataType: string;
    isRequired: boolean;
  }[];
  channels?: Channel[];
  accessCode?: string | null;
  floorplanPresetsId?: string[];
  drinksIncluded?: number;
  onlinePrice?: string;
  offlinePrice?: string;
}
